import { createContext, useContext, useState, useEffect } from "react";

const LOCAL_MODE = false; // false = use real backend

const TEST_USER = {
  name: "Test User",
  email: "test@example.com",
  password: "test123",
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (LOCAL_MODE) {
      const saved = localStorage.getItem("pdfstudio_local_user");
      if (saved) setUser(JSON.parse(saved));
    } else {
      const saved = localStorage.getItem("pdfstudio_user");
      if (saved) setUser(JSON.parse(saved));
    }
  }, []);

  // ✅ SAFE JSON PARSER
  const parseJSON = async (res) => {
    const text = await res.text();
    console.log("AUTH RAW RESPONSE:", text);

    try {
      return text ? JSON.parse(text) : {};
    } catch {
      throw new Error("Invalid JSON from server");
    }
  };

  // ─────────────────────────────
  // 🔐 LOGIN
  // ─────────────────────────────
  const login = async (email, password) => {
    if (LOCAL_MODE) {
      if (email === TEST_USER.email && password === TEST_USER.password) {
        setUser(TEST_USER);
        localStorage.setItem("pdfstudio_local_user", JSON.stringify(TEST_USER));
        return { user: TEST_USER };
      }
      throw new Error("Invalid credentials");
    }

    setLoading(true);
    try {
      const res = await fetch("/api/login.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await parseJSON(res);

      if (!res.ok || data.error) {
        throw new Error(data.error || "Login failed");
      }

      setUser(data.user);
      localStorage.setItem("pdfstudio_user", JSON.stringify(data.user));

      return data;
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────
  // 📝 SIGNUP (FIXED HERE)
  // ─────────────────────────────
  const signup = async (name, email, password) => {
    if (LOCAL_MODE) {
      if (email === TEST_USER.email) throw new Error("User already exists");
      throw new Error("Only test@example.com is allowed in local mode");
    }

    setLoading(true);
    try {
      const res = await fetch("/api/register.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          password,
        }),
      });

      const data = await parseJSON(res);

      if (!res.ok || data.error) {
        throw new Error(data.error || "Signup failed");
      }

      // ✅ Auto login after signup
      return await login(email, password);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────
  // 🚪 LOGOUT
  // ─────────────────────────────
  const logout = () => {
    if (LOCAL_MODE) {
      localStorage.removeItem("pdfstudio_local_user");
    } else {
      localStorage.removeItem("pdfstudio_user");
    }
    setUser(null);
  };

  // ─────────────────────────────
  // 🔄 REFRESH USER
  // ─────────────────────────────
  const refreshUser = () => {
    if (LOCAL_MODE) {
      const saved = localStorage.getItem("pdfstudio_local_user");
      if (saved) setUser(JSON.parse(saved));
    } else {
      const saved = localStorage.getItem("pdfstudio_user");
      if (saved) setUser(JSON.parse(saved));
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
